import React, { useCallback } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import Saas404 from '../components/common/saas/Saas404';

function getPathForTenant(tenantName: string, path: string) {
  if (path.startsWith(tenantName)) {
    // already prefixed
    return path;
  }
  return `/${tenantName}${path}`;
}

/**
 * Append the current URL's `iam` (edit-as) param onto a path. Read from
 * window at call time — no useLocation dependency, so callers outside the
 * Router tree still work. Recognises both `?iam=` and the create entry
 * point's `?init=base64({iam:...})`. If the path already carries its own
 * `iam`, leaves it alone (caller intent wins).
 *
 * Opt-in only — exposed via `useTenantPath().withIam` so callsites that
 * navigate within the same listing/edit-as context (Photos/Tours/Documents
 * controls, MediaCenter back button, MediaTypeTab links) preserve the
 * impersonation. Unrelated navigations should keep using `getPath` alone.
 */
function appendCurrentIam(path: string): string {
  if (typeof window === 'undefined') return path;
  const hashIdx = path.indexOf('#');
  const base = hashIdx === -1 ? path : path.slice(0, hashIdx);
  if (/[?&]iam=/.test(base)) return path;

  const params = new URLSearchParams(window.location.search);
  let iam = params.get('iam');
  if (!iam) {
    const init = params.get('init');
    if (init) {
      try {
        const decoded = JSON.parse(atob(init));
        if (typeof decoded?.iam === 'string') iam = decoded.iam;
      } catch {
        // ignore malformed init
      }
    }
  }
  if (!iam) return path;

  const hash = hashIdx === -1 ? '' : path.slice(hashIdx);
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}iam=${encodeURIComponent(iam)}${hash}`;
}

// Helper hook for generating tenant-aware URLs
export const useTenantPath = () => {
  const { isAddEditHost, tenant } = useTenant();

  const getTenantRoute = useCallback(
    (path: string, element: React.ReactNode) => {
      // For addedit domain, we need both:
      // - The prefixed route (/tenant/path)
      // - A redirect from /path to /tenant/path
      if (isAddEditHost()) {
        const location = getPathForTenant(tenant, path);
        return (
          <>
            <Route key={`prefixed-${path}`} path={location} element={element} />
            <Route
              key={path}
              path={path}
              element={<Navigate to={location} replace />}
            />
          </>
        );
      }

      // For regular domains, just use the path as-is
      return <Route key={path} path={path} element={element} />;
    },
    [isAddEditHost, tenant],
  );

  const getPath = useCallback(
    (path: string) => {
      if (isAddEditHost()) {
        return getPathForTenant(tenant, path);
      }
      return path;
    },
    [isAddEditHost, tenant],
  );

  // Opt-in helper for impersonation-preserving navigation. Use in place of
  // (or wrapped around) `getPath` only when the destination should inherit
  // the active edit-as target. See `appendCurrentIam` for the param sources.
  const withIam = useCallback((path: string) => appendCurrentIam(path), []);

  const getTenantLandingPage = useCallback(
    (fallback: React.ReactNode) => {
      if (isAddEditHost()) {
        /**
         * If a subscriber goes to https://addedit.mynextgensuite.com/ directly
         * for now, we will show a '404' -- eventually this will be a marketing
         * page or sign-up page
         */
        return <Saas404 target={'LANDING'} />;
      }
      return fallback;
    },
    [isAddEditHost],
  );

  const getTenantRootRoute = useCallback(() => {
    if (isAddEditHost()) {
      // For addedit domain, handle the root tenant path
      // e.g., /mlsnow should redirect to /mlsnow/create
      const tenantRoot = `/${tenant}`;
      const tenantCreate = `/${tenant}/create`;

      return (
        <Route
          key="tenant-root"
          path={tenantRoot}
          element={<Navigate to={tenantCreate} replace />}
        />
      );
    }
    // For regular domains, this isn't needed as "/" handles it
    return null;
  }, [isAddEditHost, tenant]);

  return {
    getPath,
    withIam,
    getTenantRoute,
    getTenantLandingPage,
    getTenantRootRoute,
  };
};
